import { useState } from 'react';
import styled from 'styled-components';
import { useBrandContext, BrandTabKey } from './brandShared';

// Panel "Brand at a glance" del Overview: % de completitud del perfil, qué falta
// (chips clickeables que saltan a la tab donde se completa) y un snapshot de lo
// básico (logo, paleta, datos clave). Vive aparte de BrandTabOverview porque ese
// archivo ya supera las 500 LOC.

// Cada ítem suma al % y, si falta, aparece como chip que linkea a su tab.
interface GlanceItem {
  label: string;
  done: boolean;
  tab: BrandTabKey;
}

const Card = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 12px;
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const Head = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: 2px;
`;

const Subtitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Pct = styled.div<{ $tone: 'low' | 'mid' | 'high' }>`
  font-size: 30px;
  font-weight: 800;
  line-height: 1;
  color: ${({ theme, $tone }) =>
    $tone === 'high' ? theme.colors.success : $tone === 'mid' ? theme.colors.primary : '#D97706'};
`;

const Bar = styled.div`
  width: 100%;
  height: 8px;
  background: ${({ theme }) => theme.colors.surfaceHover};
  border-radius: 4px;
  overflow: hidden;
  margin: ${({ theme }) => theme.spacing.md} 0 ${({ theme }) => theme.spacing.lg};
`;

const Fill = styled.div<{ $p: number; $tone: 'low' | 'mid' | 'high' }>`
  height: 100%;
  width: ${({ $p }) => $p}%;
  border-radius: 4px;
  transition: width 0.5s ease;
  background: ${({ theme, $tone }) =>
    $tone === 'high' ? theme.colors.success : $tone === 'mid' ? theme.colors.primary : '#D97706'};
`;

const Snapshot = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
`;

const LogoBox = styled.div`
  width: 84px;
  height: 84px;
  border-radius: 10px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  overflow: hidden;

  img { width: 100%; height: 100%; object-fit: contain; }
`;

const LogoEmpty = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  padding: 0 6px;
`;

const Basics = styled.div`
  flex: 1;
  min-width: 240px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Fact = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  line-height: 1.4;

  span { color: ${({ theme }) => theme.colors.textMuted}; font-weight: 600; margin-right: 6px; }
`;

const FactMuted = styled(Fact)`
  color: ${({ theme }) => theme.colors.textMuted};
  font-style: italic;
`;

const Palette = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 4px;
`;

const Sw = styled.div<{ $c: string }>`
  width: 26px;
  height: 26px;
  border-radius: 6px;
  background: ${({ $c }) => $c};
  border: 1px solid ${({ theme }) => theme.colors.border};
`;

const Missing = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const MissingLabel = styled.p`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Chip = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;

  &::before { content: '+'; font-weight: 800; color: ${({ theme }) => theme.colors.primary}; }
  &:hover { border-color: ${({ theme }) => theme.colors.primary}; color: ${({ theme }) => theme.colors.primary}; }
  &:focus-visible { outline: 2px solid ${({ theme }) => theme.colors.primary}; outline-offset: 2px; }
`;

const Complete = styled.div`
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.success};
`;

const truncate = (s: string, n: number) => (s.length > n ? `${s.slice(0, n)}…` : s);

export function BrandOverviewGlance() {
  const { form, workspace, assets, setActiveTab } = useBrandContext();
  const [logoBroken, setLogoBroken] = useState(false);
  const f = form.formData;

  const logoUrl = workspace?.logoUrl || assets.assets.find((a) => a.isLogo)?.url || '';
  const hasLogo = !!logoUrl;
  const colors = f.brandColors || [];
  const v = f.voiceGuide;
  const contactBits = [f.phoneNumber, f.whatsapp, f.email, f.address].filter((x) => x && x.trim());

  // Esenciales que componen el % (10 ítems → porcentaje limpio).
  const items: GlanceItem[] = [
    { label: 'Logo', done: hasLogo, tab: 'assets' },
    { label: 'Business info', done: !!(f.businessOverview || f.description), tab: 'identity' },
    { label: 'Audience', done: !!(f.targetAudience || f.customerProfile), tab: 'identity' },
    { label: 'Positioning', done: !!f.marketPositioning, tab: 'identity' },
    { label: 'Products / services', done: (f.productsServices?.length || 0) > 0, tab: 'identity' },
    { label: 'Contact', done: contactBits.length > 0, tab: 'identity' },
    { label: 'Voice & tone', done: !!(v?.tone || v?.purpose || f.tone), tab: 'voice' },
    { label: 'Content topics', done: (f.postTopics?.length || 0) > 0 || (f.keywords?.length || 0) > 0, tab: 'voice' },
    { label: 'Color palette', done: colors.length > 0, tab: 'visual' },
    { label: 'Fonts', done: (f.brandFonts?.length || 0) > 0, tab: 'visual' },
  ];

  const done = items.filter((i) => i.done).length;
  const pct = Math.round((done / items.length) * 100);
  const tone: 'low' | 'mid' | 'high' = pct >= 80 ? 'high' : pct >= 40 ? 'mid' : 'low';
  const missing = items.filter((i) => !i.done);

  const businessLine = f.businessOverview || f.description || '';
  const audienceLine = f.targetAudience || f.customerProfile || '';

  return (
    <Card>
      <Head>
        <div>
          <Title>Brand at a glance</Title>
          <Subtitle>How complete your profile is — and what's left</Subtitle>
        </div>
        <Pct $tone={tone}>{pct}%</Pct>
      </Head>

      <Bar><Fill $p={pct} $tone={tone} /></Bar>

      <Snapshot>
        <LogoBox>
          {hasLogo && !logoBroken ? (
            <img src={logoUrl} alt="Brand logo" onError={() => setLogoBroken(true)} />
          ) : (
            <LogoEmpty>No logo</LogoEmpty>
          )}
        </LogoBox>
        <Basics>
          {businessLine ? (
            <Fact><span>About</span>{truncate(businessLine, 160)}</Fact>
          ) : (
            <FactMuted>No business description yet</FactMuted>
          )}
          {audienceLine ? (
            <Fact><span>Audience</span>{truncate(audienceLine, 120)}</Fact>
          ) : (
            <FactMuted>No audience defined yet</FactMuted>
          )}
          {contactBits.length > 0 ? (
            <Fact><span>Contact</span>{truncate(contactBits.join(' · '), 120)}</Fact>
          ) : (
            <FactMuted>No contact info yet</FactMuted>
          )}
          {colors.length > 0 ? (
            <Palette>
              {colors.slice(0, 8).map((c, i) => <Sw key={i} $c={c} title={c} />)}
            </Palette>
          ) : (
            <FactMuted>No color palette yet</FactMuted>
          )}
        </Basics>
      </Snapshot>

      {missing.length > 0 ? (
        <Missing>
          <MissingLabel>Still missing — click to complete</MissingLabel>
          <Chips>
            {missing.map((m) => (
              <Chip key={m.label} type="button" onClick={() => setActiveTab(m.tab)}>{m.label}</Chip>
            ))}
          </Chips>
        </Missing>
      ) : (
        <Complete>✓ Your brand profile is complete</Complete>
      )}
    </Card>
  );
}
